import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonControl,
  SidePanelFormControl,
  TabPaneControl,
  TableControl,
} from 'spark-core-dx/components';
import { useGenericManager } from 'spark-core-dx/hooks';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import AIPanel from '../../components/AIChat/AIPanel';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import {
  getFirstMetadata,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
} from './clientEntryContracts';
import {
  CLIENT_BILLING_ITEM_NAMES,
  CLIENT_BILLING_OPERATIONS,
  CLIENT_BILLING_PROFILE_FIELDS,
  createBillingControlDraft,
  createManualBillingDraft,
  createManualBillingFields,
  formatBillingCurrency,
  formatBillingDate,
  formatBillingPeriodDate,
  normalizeBillingAIManageProposal,
  selectBillingFields,
  shiftBillingPeriod,
} from './clientBillingContracts';

const runBillingOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const clone = (value) => (
  value == null ? value : JSON.parse(JSON.stringify(value))
);

const metric = (label, value) => (
  <div className="resource-workspace-metric" key={label}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const BillingState = ({ loading, error, message }) => {
  if (loading) {
    return (
      <ResourceStateControl
        loading
        loadingText="Loading the client billing subledger..."
        compact
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl message={error} tone="error" compact />
    );
  }
  return message ? (
    <div
      className="resource-workspace-access-note resource-workspace-access-note--success"
      role="status"
    >
      {message}
    </div>
  ) : null;
};

const BillingExceptionList = ({ exceptions }) => (
  <section className="resource-workspace-panel">
    {(exceptions || []).length ? (
      <div className="resource-workspace-record-list">
        {exceptions.map((exception) => (
          <article
            className="resource-workspace-record-row resource-workspace-record-row--stacked"
            key={exception}
          >
            <div><strong>{exception}</strong></div>
          </article>
        ))}
      </div>
    ) : (
      <ResourceStateControl
        message="No billing exceptions are recorded for this period."
        compact
      />
    )}
  </section>
);

const BillingReconciliation = ({ totals, currency }) => (
  <section className="resource-workspace-two-column">
    <article className="resource-workspace-panel">
      <div className="resource-workspace-section-title">
        <div>
          <h3>Financial equation</h3>
          <p>
            Gross − discount + tax + fees − credits + adjustments + rounding
            = net operational billing.
          </p>
        </div>
      </div>
      <div className="command-billing__equation">
        <span>{formatBillingCurrency(totals?.GrossAmount, currency)} gross</span>
        <span>− {formatBillingCurrency(totals?.DiscountAmount, currency)} discount</span>
        <span>+ {formatBillingCurrency(totals?.TaxAmount, currency)} tax</span>
        <span>+ {formatBillingCurrency(totals?.FeeAmount, currency)} fees</span>
        <span>− {formatBillingCurrency(totals?.CreditAmount, currency)} credits</span>
        <span>+ {formatBillingCurrency(totals?.AdjustmentAmount, currency)} adjustments</span>
        <span>+ {formatBillingCurrency(totals?.RoundingAmount, currency)} rounding</span>
        <strong>= {formatBillingCurrency(totals?.NetAmount, currency)} net</strong>
      </div>
    </article>
    <article className="resource-workspace-panel">
      <div className="resource-workspace-section-title">
        <h3>Control totals</h3>
      </div>
      <div className="command-billing__control-grid">
        <span>SKU rows</span><strong>{totals?.RowCount ?? 0}</strong>
        <span>Measured quantity</span><strong>{totals?.MeasuredQuantity ?? 0}</strong>
        <span>Billable quantity</span><strong>{totals?.BillableQuantity ?? 0}</strong>
        <span>Reviewed</span><strong>{totals?.ReviewedCount ?? 0}</strong>
        <span>Approved</span><strong>{totals?.ApprovedCount ?? 0}</strong>
        <span>Closed</span><strong>{totals?.ClosedCount ?? 0}</strong>
        <span>Reconciliation variance</span>
        <strong>{formatBillingCurrency(totals?.ReconciliationVariance, currency)}</strong>
      </div>
    </article>
  </section>
);

const ClientBillingScreen = ({
  clientId,
  currentEnvironment,
  workspaceHeader = null,
}) => {
  const navigate = useNavigate();
  const workspaceManager = useGenericManager(
    CLIENT_BILLING_ITEM_NAMES.workspace,
  );
  const profileManager = useGenericManager(
    CLIENT_BILLING_ITEM_NAMES.profile,
  );
  const ledgerManager = useGenericManager(
    CLIENT_BILLING_ITEM_NAMES.ledger,
  );
  const manualManager = useGenericManager(
    CLIENT_BILLING_ITEM_NAMES.manualRequest,
  );
  const controlManager = useGenericManager(
    CLIENT_BILLING_ITEM_NAMES.controlRequest,
  );
  const managerRefs = useRef({});
  managerRefs.current = {
    workspaceManager,
    profileManager,
    ledgerManager,
    manualManager,
    controlManager,
  };

  const [workspace, setWorkspace] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [periodRequest, setPeriodRequest] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [configurationDraft, setConfigurationDraft] = useState(null);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [controlDraft, setControlDraft] = useState(null);
  const [controlOpen, setControlOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [allClientsOpen, setAllClientsOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const managers = managerRefs.current;
      const [
        loaded,
        profileMetadata,
        ledgerMetadata,
        manualMetadata,
        controlMetadata,
      ] = await Promise.all([
        runBillingOperation(
          managers.workspaceManager,
          CLIENT_BILLING_OPERATIONS.load,
          {
            ClientId: clientId,
            CurrentEnvironment: currentEnvironment,
            ...periodRequest,
          },
          'Client billing is unavailable.',
        ),
        managers.profileManager.GetMetadata({ forceReload: true }),
        managers.ledgerManager.GetMetadata({ forceReload: true }),
        managers.manualManager.GetMetadata({ forceReload: true }),
        managers.controlManager.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(loaded);
      setMetadata({
        profile: getFirstMetadata(profileMetadata, managers.profileManager),
        ledger: getFirstMetadata(ledgerMetadata, managers.ledgerManager),
        manual: getFirstMetadata(manualMetadata, managers.manualManager),
        control: getFirstMetadata(controlMetadata, managers.controlManager),
      });
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client billing is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [
    clientId,
    currentEnvironment,
    periodRequest,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const execute = async (
    operationName,
    additions,
    fallbackMessage,
    successMessage,
    errorSurface = 'page',
  ) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const loaded = await runBillingOperation(
        managerRefs.current.workspaceManager,
        operationName,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          PeriodStartUtc: workspace?.PeriodStartUtc,
          PeriodEndUtc: workspace?.PeriodEndUtc,
          ...additions,
        },
        fallbackMessage,
      );
      setWorkspace(loaded);
      setMessage(successMessage);
      return { Success: true, Items: [loaded] };
    } catch (ex) {
      const failureMessage = ex?.message || fallbackMessage;
      if (errorSurface === 'page') {
        setError(failureMessage);
      }
      return {
        Success: false,
        DisplayMessage: failureMessage,
      };
    } finally {
      setBusy(false);
    }
  };

  const openConfiguration = () => {
    setConfigurationDraft(clone(workspace?.Profile) || {
      ClientId: clientId,
      EnvironmentKey: currentEnvironment,
      DisplayName: `${workspace?.ClientName || 'Client'} Billing`,
      BillingPeriodType: 1,
      BillingPeriodAnchorValue: 1,
      TransactionMode: 1,
      Currency: 'USD',
      BillingEnabled: true,
      CalculationCadence: 1,
      CalculationCadenceAnchorValue: 1,
      EffectiveStartDateUtc: new Date().toISOString(),
      SkuConfigurations: [],
      RateRules: [],
    });
    setConfigurationOpen(true);
  };

  const saveConfiguration = async (updated) => {
    const result = await execute(
      CLIENT_BILLING_OPERATIONS.saveConfiguration,
      { Profile: updated },
      'Billing configuration was not saved.',
      'Billing configuration is now active for this client and Current Environment.',
    );
    if (result.Success) {
      setConfigurationOpen(false);
      setConfigurationDraft(null);
    }
    return result;
  };

  const calculate = async (allClients = false) => {
    const result = await execute(
      CLIENT_BILLING_OPERATIONS.calculate,
      {
        AllClients: allClients,
        AsOfUtc: new Date().toISOString(),
        CalculationTrigger: allClients ? 'Scheduled' : 'Manual',
      },
      'Billing calculation failed.',
      allClients
        ? 'All due clients were calculated with per-client isolation.'
        : 'The selected client subledger was recalculated as of now.',
      allClients ? 'dialog' : 'page',
    );
    if (result.Success) setAllClientsOpen(false);
    return result;
  };

  const openManual = () => {
    setManualDraft(
      createManualBillingDraft(workspace?.ActiveProductOffers || []),
    );
    setManualOpen(true);
  };

  const addManual = async (updated) => {
    const result = await execute(
      CLIENT_BILLING_OPERATIONS.addManualRow,
      { ManualRowRequest: updated },
      'The manual SKU component was not added.',
      'The manual component was added to the selected Product Offer SKU row.',
    );
    if (result.Success) {
      setManualOpen(false);
      setManualDraft(null);
    }
    return result;
  };

  const openControl = (action, row = null) => {
    setControlDraft(createBillingControlDraft(action, row?._id || ''));
    setControlOpen(true);
  };

  const handleBillingAIUpdate = (uiAction, item, value) => {
    if (String(uiAction || '').trim().toLowerCase() !== 'manage') return;
    const proposal = normalizeBillingAIManageProposal(value);
    if (!proposal) return;
    if (proposal.surface === 'configuration' && workspace?.CanConfigure) {
      openConfiguration();
      return;
    }
    if (proposal.surface === 'manual' && workspace?.CanAddManualRows) {
      openManual();
      return;
    }
    if (proposal.surface !== 'control' || !workspace?.CanControlPeriod) return;
    const row = proposal.ledgerRowId
      ? (workspace?.LedgerRows || []).find(
        (entry) => String(entry?._id) === proposal.ledgerRowId,
      )
      : null;
    if (proposal.ledgerRowId && !row) return;
    openControl(proposal.action, row);
  };

  const applyControl = async (updated) => {
    const result = await execute(
      CLIENT_BILLING_OPERATIONS.setControl,
      { ControlRequest: updated },
      'The billing control was not applied.',
      `Billing control '${updated.Action}' was applied.`,
    );
    if (result.Success) {
      setControlOpen(false);
      setControlDraft(null);
    }
    return result;
  };

  const loadSources = async (row) => {
    const result = await execute(
      CLIENT_BILLING_OPERATIONS.loadSources,
      {
        SourceResearch: { LedgerRowId: row._id },
      },
      'Source evidence is unavailable.',
      `Source evidence loaded for SKU ${row.ProductOfferSku}.`,
    );
    if (result.Success) setSourceOpen(true);
  };

  const rowActions = (row) => {
    const actions = [{
      name: 'Research Sources',
      customFunction: loadSources,
    }];
    if (!workspace?.CanControlPeriod || Number(row.ReviewStatus) >= 3) {
      return actions;
    }
    actions.push({
      name: row.ManualCalculationHold ? 'Release Hold' : 'Place Hold',
      customFunction: () => openControl(
        row.ManualCalculationHold ? 'ReleaseHold' : 'Hold',
        row,
      ),
    });
    actions.push({
      name: row.SystemChangeLocked ? 'Unlock System Changes' : 'Lock System Changes',
      customFunction: () => openControl(
        row.SystemChangeLocked ? 'Unlock' : 'Lock',
        row,
      ),
    });
    if (Number(row.ReviewStatus) === 1 && !row.HasException) {
      actions.push({
        name: 'Mark Reviewed',
        customFunction: () => openControl('Review', row),
      });
    }
    return actions;
  };

  const profileFields = useMemo(
    () => selectBillingFields(
      metadata.profile,
      CLIENT_BILLING_PROFILE_FIELDS,
    ),
    [metadata.profile],
  );
  const manualFields = useMemo(
    () => createManualBillingFields(
      metadata.manual,
      workspace?.ActiveProductOffers,
    ),
    [metadata.manual, workspace?.ActiveProductOffers],
  );
  const controlFields = useMemo(
    () => metadata.control?.Fields ?? metadata.control?.fields ?? [],
    [metadata.control],
  );

  const totals = workspace?.ControlTotals || {};
  const currency = totals.Currency || workspace?.Profile?.Currency || 'USD';
  const billingAIPayload = {
    PeriodStartUtc: workspace?.PeriodStartUtc || periodRequest.PeriodStartUtc || null,
    PeriodEndUtc: workspace?.PeriodEndUtc || periodRequest.PeriodEndUtc || null,
  };
  const ledgerTab = (
    <section className="resource-workspace-panel command-billing__ledger">
      <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
        <div>
          <h3>Operational SKU subledger</h3>
          <p>
            One working row per Client, Current Environment, period, and Product
            Offer SKU. Grid editing is disabled; guarded actions preserve evidence.
          </p>
        </div>
        <strong>{workspace?.LedgerRows?.length ?? 0} rows</strong>
      </div>
      {metadata.ledger ? (
        <TableControl
          data={workspace?.LedgerRows || []}
          defaultNumRows={50}
          disableTableTitle
          enableMultiSelect={false}
          enableSidePanelEdit={false}
          filters={ledgerManager?.filters}
          loadingState={loading || busy}
          metadata={[metadata.ledger]}
          mgr={ledgerManager}
          customQuickViewList={rowActions}
        />
      ) : null}
    </section>
  );
  const commandBarActions = [
    {
      key: 'previous-period',
      label: 'Previous Period',
      iconKey: 'previous',
      disabled: !workspace || loading || busy,
      onClick: () => {
        const shifted = shiftBillingPeriod(
          workspace.PeriodStartUtc,
          workspace.PeriodEndUtc,
          -1,
          Number(workspace?.Profile?.BillingPeriodType) === 2,
        );
        setPeriodRequest(shifted);
      },
    },
    {
      key: 'current-period',
      label: 'Current Period',
      iconKey: 'clock',
      disabled: loading || busy || !Object.keys(periodRequest).length,
      onClick: () => setPeriodRequest({}),
    },
    {
      key: 'next-period',
      label: 'Next Period',
      iconKey: 'next',
      disabled: !workspace || loading || busy,
      onClick: () => {
        const shifted = shiftBillingPeriod(
          workspace.PeriodStartUtc,
          workspace.PeriodEndUtc,
          1,
          Number(workspace?.Profile?.BillingPeriodType) === 2,
        );
        setPeriodRequest(shifted);
      },
    },
    {
      key: 'refresh-billing',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: refresh,
      separatorAfter: true,
    },
    ...(workspace?.CanConfigure ? [{
      key: 'configure-billing',
      label: 'Configure Billing',
      iconKey: 'settings',
      disabled: busy || !metadata.profile,
      onClick: openConfiguration,
    }] : []),
    ...(workspace?.CanCalculate ? [{
      key: 'calculate-billing',
      label: 'Calculate As of Now',
      iconKey: 'money',
      disabled: busy,
      onClick: () => calculate(false),
    }] : []),
    ...(workspace?.CanCalculateAllClients ? [{
      key: 'calculate-all-clients',
      label: 'Calculate All Due Clients',
      iconKey: 'clientscreen',
      disabled: busy,
      onClick: () => setAllClientsOpen(true),
    }] : []),
    ...(workspace?.CanAddManualRows ? [{
      key: 'add-manual-sku',
      label: 'Add Manual SKU Component',
      iconKey: 'add',
      disabled: busy || !metadata.manual
        || !(workspace.ActiveProductOffers || []).length,
      onClick: openManual,
    }] : []),
    ...(workspace?.CanControlPeriod ? [
      {
        key: 'review-billing-period',
        label: 'Review Period',
        iconKey: 'validate',
        disabled: busy || !workspace.LedgerRows?.length,
        onClick: () => openControl('Review'),
      },
      {
        key: 'approve-billing-period',
        label: 'Approve & Freeze',
        iconKey: 'lock',
        disabled: busy || !workspace.LedgerRows?.length,
        onClick: () => openControl('Approve'),
      },
      {
        key: 'close-billing-period',
        label: 'Close Convoke Period',
        iconKey: 'close',
        disabled: busy || !workspace.LedgerRows?.length,
        onClick: () => openControl('ClosePeriod'),
      },
    ] : []),
  ];

  return (
    <AIPanel
      open
      presentation="screen-workspace"
      workspaceHeader={workspaceHeader}
      operationName={CLIENT_BILLING_OPERATIONS.aiQuestion}
      referenceId={`${clientId}:${currentEnvironment}`}
      referenceName="ClientBilling"
      params={{
        clientId,
        currentEnvironment,
        periodStartUtc: billingAIPayload.PeriodStartUtc,
        periodEndUtc: billingAIPayload.PeriodEndUtc,
      }}
      payload={billingAIPayload}
      onUpdatePanel={handleBillingAIUpdate}
      chatPane="right"
      chatPaneCollapseMode="auto"
      chatPaneStartOpen={
        configurationOpen
        || manualOpen
        || controlOpen
        || sourceOpen
        || allClientsOpen
          ? false
          : null
      }
      aiProcessingMode="Direct"
      singleSessionMode
      planningPreviewEnabled={false}
      previewPanelEnabled={false}
      splashMessage="Ask AI to explain the current billing period or prepare a billing management panel."
      introMessage="I use only the current server-authorized Billing evidence. I can prepare an existing management panel for your review, but I never calculate, save, approve, close, or post automatically."
      minLeft={360}
      minRight={360}
      panelStorageKey={null}
    >
      <div className="resource-workspace-overview command-entry command-billing">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Billing actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />

      <BillingState loading={loading} error={error} message={message} />

      {!loading && workspace ? (
        <>
          <div className="resource-workspace-access-note" role="status">
            <strong>{workspace.ClientName || 'Selected client'} billing</strong>
            <span>
              {' — '}
              {formatBillingPeriodDate(workspace.PeriodStartUtc, 'Period start')}
              {' — '}
              {formatBillingPeriodDate(workspace.PeriodEndUtc, 'Period end')}
              {' — '}
              Calculated as of {formatBillingDate(totals.CalculatedAsOfUtc)}
            </span>
          </div>

          <div className="resource-workspace-metric-grid" aria-label="Billing control totals">
            {metric('Product Offer SKUs', totals.SkuCount ?? 0)}
            {metric('Billable Quantity', totals.BillableQuantity ?? 0)}
            {metric('Gross', formatBillingCurrency(totals.GrossAmount, currency))}
            {metric('Net', formatBillingCurrency(totals.NetAmount, currency), 'positive')}
            {metric(
              'Exceptions',
              totals.ExceptionCount ?? 0,
              totals.ExceptionCount ? 'negative' : '',
            )}
            {metric(
              'Holds',
              totals.HoldCount ?? 0,
              totals.HoldCount ? 'warning' : '',
            )}
            {metric('Locked / Frozen', totals.LockedCount ?? 0)}
            {metric(
              'Variance',
              formatBillingCurrency(totals.ReconciliationVariance, currency),
              Number(totals.ReconciliationVariance) ? 'negative' : 'positive',
            )}
          </div>

          <TabPaneControl
            align="left"
            defaultKey="ledger"
            storageKey={`client-command-billing:${clientId}`}
            tabs={[
              { key: 'ledger', title: 'SKU Ledger', content: ledgerTab },
              {
                key: 'exceptions',
                title: `Exceptions & Holds (${totals.ExceptionCount ?? 0})`,
                content: <BillingExceptionList exceptions={workspace.Exceptions} />,
              },
              {
                key: 'reconciliation',
                title: 'Reconciliation',
                content: (
                  <BillingReconciliation totals={totals} currency={currency} />
                ),
              },
            ]}
          />

        </>
      ) : null}

      <SidePanelFormControl
        open={configurationOpen}
        title="Configure Client Billing"
        metadata={metadata.profile}
        formFields={profileFields}
        itemData={configurationDraft}
        onSave={saveConfiguration}
        onCancel={() => setConfigurationOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Saving...' : 'Save Configuration'}
        setOpenEditSidePanel={setConfigurationOpen}
        setItemData={setConfigurationDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={manualOpen}
        title="Add Manual SKU Component"
        metadata={metadata.manual}
        formFields={manualFields}
        itemData={manualDraft}
        onSave={addManual}
        onCancel={() => setManualOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Adding...' : 'Add Component'}
        setOpenEditSidePanel={setManualOpen}
        setItemData={setManualDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={controlOpen}
        title="Billing Control Action"
        metadata={metadata.control}
        formFields={controlFields}
        itemData={controlDraft}
        onSave={applyControl}
        onCancel={() => setControlOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Applying...' : 'Apply Control'}
        setOpenEditSidePanel={setControlOpen}
        setItemData={setControlDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={sourceOpen}
        title={`Source research — ${workspace?.SourceResearch?.ProductOfferSku || 'SKU'}`}
        subTitle="Immutable calculation evidence and navigation back to the authoritative records."
        okText="Close"
        cancelText="Close"
        onCancel={() => setSourceOpen(false)}
        onX={() => setSourceOpen(false)}
        onOk={() => setSourceOpen(false)}
      >
        <div className="resource-workspace-record-list command-billing__sources">
          <code>{workspace?.SourceResearch?.SourceManifestHash}</code>
          {(workspace?.SourceResearch?.Sources || []).map((source) => (
            <article
              className="resource-workspace-record-row resource-workspace-record-row--stacked"
              key={`${source.SourceItemName}-${source.SourceRecordId}`}
            >
              <div>
                <strong>{source.SourceItemName}</strong>
                <span>{source.Description}</span>
                <small>
                  {source.SourceRecordId} · {source.Quantity} ·
                  {' '}{formatBillingCurrency(source.Amount, currency)}
                </small>
              </div>
              <div className="resource-workspace-record-row-actions">
                {source.SourceRoute ? (
                  <ButtonControl
                    type="standard-small"
                    onClick={() => navigate(source.SourceRoute)}
                  >
                    Open Source Record
                  </ButtonControl>
                ) : (
                  <small>Navigation is unavailable for this source type or permission.</small>
                )}
              </div>
            </article>
          ))}
        </div>
      </DialogControl>

      <DialogControl
        openDialog={allClientsOpen}
        title="Calculate all due clients"
        subTitle="Runs only due scheduled profiles in the Current Environment with per-client failure isolation."
        okText="Calculate Due Clients"
        cancelText="Cancel"
        onCancel={() => setAllClientsOpen(false)}
        onX={() => setAllClientsOpen(false)}
        onOk={() => calculate(true)}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Continue with the bounded all-client calculation? Existing holds,
          locks, approved rows, and closed periods remain protected.
        </p>
      </DialogControl>
      </div>
    </AIPanel>
  );
};

export default ClientBillingScreen;
